// components/configuration/tabs/NotificationInfoTab.jsx
import React from 'react';
import { getNotificationRulesByRole } from '../../../constants/notificationRulesByRole';

/**
 * User notification information tab
 * Shows which notification rules apply to the current user
 */
const NotificationInfoTab = ({ userRole, onNavigateToRules }) => {
  const notificationRules = getNotificationRulesByRole(userRole);

  const colorClasses = {
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700'
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-700'
    },
    indigo: {
      bg: 'bg-indigo-50',
      border: 'border-indigo-200',
      text: 'text-indigo-700'
    }
  };

  return (
    <div className="space-y-6">
      {/* Description */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">My Notifications</h2>
        <p className="text-sm text-gray-600 mb-6">
          You will receive notifications for all enabled rules that match your role and dashboard permissions.
          {userRole === 'admin' && (
            <> As an admin, you can manage which rules are enabled in the{' '}
              <button 
                onClick={onNavigateToRules}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Notification Rules
              </button> tab.
            </>
          )}
        </p>
        
        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">How Notifications Work</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Administrators control which notification rules are active</li>
            <li>• You'll see notifications based on your role ({userRole}) and dashboard access</li>
            <li>• You can mark notifications as read or dismiss them individually</li>
            <li>• Dismissed notifications are hidden from your view but not deleted</li>
          </ul>
        </div>
      </div>

      {/* Available Rules */}
      <div>
        <h3 className="text-md font-semibold text-gray-900 mb-3">
          Notification Rules Available to You
        </h3>
        <p className="text-sm text-gray-600 mb-3">
          Based on your role and permissions, you will receive alerts from the following rule categories:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {notificationRules.map((rule) => {
            const colors = colorClasses[rule.color];
            return (
              <div 
                key={rule.category}
                className={`p-4 ${colors.bg} border ${colors.border} rounded-lg`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={`${colors.text} font-medium`}>{rule.title}</span>
                </div>
                <ul className={`text-sm ${colors.text} space-y-1`}>
                  {rule.items.map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NotificationInfoTab;
